import { cn } from "@/lib/utils";
import { ReactNode } from "react";

// ============================================
// Typography System - Makelog Style
// ============================================
// Font: Inter (system-ui fallback)
// Scale: Display(36px) H1(24px) H2(20px) H3(16px) Body(14px) Small(13px) Micro(12px)
// Mobile: Display(28px) H1(20px) H2(18px) H3(15px)
// ============================================

interface DisplayXLProps {
  children: ReactNode;
  className?: string;
}

export const DisplayXL = ({ children, className }: DisplayXLProps) => (
  <h1 className={cn(
    "text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground",
    className
  )}>
    {children}
  </h1>
);

interface DisplayLProps {
  children: ReactNode;
  className?: string;
}

export const DisplayL = ({ children, className }: DisplayLProps) => (
  <h1 className={cn(
    "text-3xl sm:text-4xl font-bold tracking-tight text-foreground",
    className
  )}>
    {children}
  </h1>
);

interface DisplayMProps {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export const DisplayM = ({ children, className, style }: DisplayMProps) => (
  <h1 className={cn(
    "text-2xl sm:text-3xl font-bold tracking-tight text-foreground",
    className
  )} style={style}>
    {children}
  </h1>
);

interface PageHeaderProps {
  children: ReactNode;
  variant?: "app" | "marketing" | "content";
  className?: string;
}

export const PageHeader = ({ children, variant = "app", className }: PageHeaderProps) => {
  const variantStyles = {
    app: "bg-primary/10 text-foreground px-4 py-1.5 rounded-full inline-block",
    marketing: "",
    content: ""
  };
  
  return (
    <h1 className={cn(
      "text-2xl font-semibold tracking-tight",
      variantStyles[variant],
      className
    )}>
      {children}
    </h1>
  );
};

interface SectionHeaderProps {
  children: ReactNode;
  className?: string;
}

export const SectionHeader = ({ children, className }: SectionHeaderProps) => (
  <h2 className={cn(
    "text-xl font-semibold tracking-tight text-foreground",
    className
  )}>
    {children}
  </h2>
);

interface SubsectionHeaderProps {
  children: ReactNode;
  className?: string;
}

export const SubsectionHeader = ({ children, className }: SubsectionHeaderProps) => (
  <h3 className={cn(
    "text-base font-medium text-foreground",
    className
  )}>
    {children}
  </h3>
);

interface BodyTextProps {
  children: ReactNode;
  variant?: "primary" | "secondary" | "large";
  className?: string;
  style?: React.CSSProperties;
}

export const BodyText = ({ children, variant = "primary", className, style }: BodyTextProps) => {
  const classes = {
    primary: "text-sm leading-relaxed text-foreground",
    secondary: "text-sm text-muted-foreground",
    large: "text-base sm:text-lg text-foreground"
  };
  return <p className={cn(classes[variant], className)} style={style}>{children}</p>;
};

interface CaptionProps {
  children: ReactNode;
  className?: string;
}

export const Caption = ({ children, className }: CaptionProps) => (
  <p className={cn(
    "text-xs text-muted-foreground",
    className
  )}>
    {children}
  </p>
);

interface LabelTextProps {
  children: ReactNode;
  className?: string;
}

export const LabelText = ({ children, className }: LabelTextProps) => (
  <span className={cn(
    "text-xs font-medium leading-none text-foreground",
    className
  )}>
    {children}
  </span>
);
